import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { UploadSimple, FilmReel, MagicWand } from "@phosphor-icons/react";
import ImageUploader from "../components/ImageUploader";
import { extractVideoThumbnail, uploadBlob } from "../lib/videoThumbnail";

const SAMPLE_VIDEOS = [
    { url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4", thumb: "https://images.pexels.com/photos/7715781/pexels-photo-7715781.jpeg" },
    { url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4", thumb: "https://images.unsplash.com/photo-1763889784402-5e8744af31b2" },
    { url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4", thumb: "https://images.unsplash.com/photo-1774988353278-b01caf489c4e" },
];

export default function Upload() {
    const nav = useNavigate();
    const [videoUrl, setVideoUrl] = useState("");
    const [thumb, setThumb] = useState("");
    const [caption, setCaption] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [generatingThumb, setGeneratingThumb] = useState(false);

    const autoGenFromBlob = async (blob) => {
        try {
            setGeneratingThumb(true);
            const thumbBlob = await extractVideoThumbnail(blob, { timeSec: 1 });
            const url = await uploadBlob(api, thumbBlob);
            setThumb(url);
            toast.success("Thumbnail auto-generated");
        } catch (err) {
            console.error("[thumb-gen-blob]", err);
            toast.error("Could not auto-generate thumbnail — add one manually");
        } finally {
            setGeneratingThumb(false);
        }
    };

    const onGenFromUrl = async () => {
        if (!videoUrl) return toast.error("Add a video URL first");
        try {
            setGeneratingThumb(true);
            const thumbBlob = await extractVideoThumbnail(videoUrl, { timeSec: 1 });
            const url = await uploadBlob(api, thumbBlob);
            setThumb(url);
            toast.success("Thumbnail generated");
        } catch (err) {
            console.error("[thumb-gen-url]", err);
            toast.error("Could not generate — video may not allow cross-origin access");
        } finally {
            setGeneratingThumb(false);
        }
    };

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 100 * 1024 * 1024) return toast.error("Max 100MB");
        const form = new FormData();
        form.append("file", f);
        setUploading(true);
        try {
            const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
            const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
            if (f.type.startsWith("video")) {
                setVideoUrl(fullUrl);
                // Auto-generate thumbnail from the original local file (no CORS issue)
                if (!thumb) autoGenFromBlob(f);
            } else {
                setThumb(fullUrl);
            }
            toast.success("Uploaded");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally {
            setUploading(false);
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!videoUrl) return toast.error("Add a video URL or upload a file");
        setSubmitting(true);
        try {
            await api.post("/videos", { video_url: videoUrl, thumbnail: thumb, caption });
            toast.success("Posted to feed");
            nav("/feed");
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setSubmitting(false); }
    };

    return (
        <div className="px-4 md:px-10 py-8 max-w-2xl mx-auto">
            <div className="font-heading text-xs tracking-[0.4em] text-wta-muted">SHOW THE WORLD</div>
            <h1 className="font-heading text-5xl md:text-6xl tracking-tight uppercase">Upload</h1>

            <form onSubmit={onSubmit} className="mt-8 space-y-4 bg-wta-surface border border-wta-border p-6 rounded-sm">
                <div>
                    <label className="text-xs tracking-widest text-wta-muted uppercase">Caption</label>
                    <input data-testid="upload-caption" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Tell us about it" className="mt-1 w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2.5 text-sm" />
                </div>
                <div>
                    <label className="text-xs tracking-widest text-wta-muted uppercase">Video URL (mp4 / webm / hosted)</label>
                    <input data-testid="upload-url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://example.com/video.mp4" className="mt-1 w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2.5 text-sm" />
                </div>
                <div>
                    <label className="text-xs tracking-widest text-wta-muted uppercase">Thumbnail (9:16)</label>
                    <input data-testid="upload-thumb" value={thumb} onChange={(e) => setThumb(e.target.value)} placeholder="https://... (or auto-generate below)" className="mt-1 w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2.5 text-sm" />
                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                        <ImageUploader
                            value={thumb}
                            onChange={setThumb}
                            aspect={9 / 16}
                            cropShape="rect"
                            outputSize={{ width: 720, height: 1280 }}
                            label="Upload Thumbnail"
                            testid="upload-thumb-pic"
                            previewClass="w-16 aspect-[9/16] rounded-sm"
                            placeholder="—"
                        />
                        <button
                            type="button"
                            data-testid="gen-thumb-btn"
                            onClick={onGenFromUrl}
                            disabled={!videoUrl || generatingThumb}
                            className="btn-ghost px-3 py-2 rounded-sm text-xs font-heading uppercase tracking-wider flex items-center gap-1 disabled:opacity-40"
                        >
                            <MagicWand size={14} /> {generatingThumb ? "Generating…" : "Auto-Generate"}
                        </button>
                    </div>
                    <div className="text-[10px] text-wta-muted mt-1">Uploaded videos auto-capture a frame. For URLs, click Auto-Generate (CORS-permitting).</div>
                </div>
                <div>
                    <label className="text-xs tracking-widest text-wta-muted uppercase">Or upload directly</label>
                    <div className="mt-1 flex items-center gap-2">
                        <input data-testid="upload-file" type="file" accept="video/*,image/*" onChange={onFile} className="text-sm" />
                        {uploading && <span className="text-xs text-wta-muted">Uploading…</span>}
                    </div>
                </div>
                <button data-testid="upload-submit" type="submit" disabled={submitting} className="w-full btn-primary py-3 rounded-sm text-base disabled:opacity-50">
                    <UploadSimple size={16} className="inline mr-1" /> {submitting ? "Posting…" : "Post to Feed"}
                </button>
            </form>

            <div className="mt-8">
                <div className="font-heading text-lg tracking-wider mb-3 flex items-center gap-2">
                    <FilmReel size={18} /> Don't have a video? Try a sample
                </div>
                <div className="grid grid-cols-3 gap-2">
                    {SAMPLE_VIDEOS.map((s, i) => (
                        <button key={i} type="button" data-testid={`sample-${i}`} onClick={() => { setVideoUrl(s.url); setThumb(s.thumb); }} className="aspect-video relative overflow-hidden rounded-sm border border-wta-border hover:border-wta-primary">
                            <img src={s.thumb} alt="" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/40" />
                            <div className="absolute inset-0 flex items-center justify-center text-xs font-heading tracking-wider">USE</div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
